import { Room, TwilioError } from 'twilio-video';
import { useEffect } from 'react';

import { Callback } from '../../../types';
import { captureTwilioDuplicateIdentityDiagnostic } from '../../../logging/twilioDiagnostics';

const DUPLICATE_IDENTITY_ERROR_CODE = 53205;

export default function useHandleRoomDisconnectionErrors(room: Room, onError: Callback) {
  useEffect(() => {
    const onDisconnected = (disconnectedRoom: Room, error: TwilioError) => {
      if (!error) {
        return;
      }

      // 53205 (duplicate identity) is recoverable — a lingering participant gets
      // kicked when a new connect lands with the same identity. Surfacing the
      // global error dialog traps the user on a modal until reload, so suppress
      // it here and let the per-page reconnect logic take over. We still record
      // the event in Sentry so we can track how often it happens.
      if (error.code === DUPLICATE_IDENTITY_ERROR_CODE) {
        captureTwilioDuplicateIdentityDiagnostic(error, 'room_disconnected', {
          roomName: disconnectedRoom?.name ?? null,
          roomSid: disconnectedRoom?.sid ?? null,
          roomState: disconnectedRoom?.state ?? null,
        });
        return;
      }

      onError(error);
    };

    room.on('disconnected', onDisconnected);
    return () => {
      room.off('disconnected', onDisconnected);
    };
  }, [room, onError]);
}
